/**
 * The review of a version's inline Markdown (DESIGN UD-31, ED-152 to ED-154): a dialog listing the version's rewritten
 * paragraphs, each with the original's Markdown (read-only, the spans that moved to replacement words or were dropped marked)
 * beside the new Markdown (editable, with Bold and Italic buttons on the selection and a reset to the automatic version).
 * Paragraphs with moved or dropped spans come first. Save stores the edits with the version (`chain-control.ts`); every
 * later download of it uses them. The analysis worker makes the review; nothing here parses a document. Text nodes only.
 */
import { signal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import {
	type InlineNode,
	type MarkdownEdit,
	type ReviewItem,
	type ReviewSpan,
	parseInline,
	plainOf,
	toggleMark,
} from '../../shared/md-review.ts';
import { addNotice } from '../state.ts';
import { REVIEW, VERSIONS } from '../strings-bench.ts';
import { HEADER } from '../strings.ts';
import { Dialog } from '../ui.tsx';
import { beginWork, endWork, nextPaint } from '../work.ts';
import { reviewMarkdown, saveMarkdownEdits, setMarkupHint, versionText } from './chain-control.ts';
import { faultReason } from './messages.ts';

/** Paragraphs shown at first in the long list, and added by each "Show more". */
const PAGE = 40;

interface Review {
	n: number;
	loading: boolean;
	error: string | null;
	items: ReviewItem[];
	moved: number;
	dropped: number;
}

/** The review on screen, or null when the dialog is closed. */
export const review = signal<Review | null>(null);
/** The operator's unsaved text per paragraph (block), for the paragraphs touched since the review opened. */
const drafts = signal<Record<number, string>>({});
const shown = signal(PAGE);
const lookShown = signal(PAGE);
const saving = signal(false);

const problem = (it: ReviewItem): boolean =>
	it.stale !== undefined || it.spans.some((x) => x.status !== 'carried');

/** The Markdown the new paragraph has now: the operator's unsaved text, else the stored edit, else the automatic one. */
const currentMd = (it: ReviewItem): string => drafts.value[it.block] ?? it.edit ?? it.auto;

/** The edits the dialog would store: every paragraph whose text differs from its automatic Markdown. */
function pendingEdits(r: Review): MarkdownEdit[] {
	const out: MarkdownEdit[] = [];
	for (const it of r.items) {
		const v = drafts.value[it.block] ?? it.edit ?? it.auto;
		if (v !== it.auto) out.push({ block: it.block, base: it.base, md: v });
	}
	return out;
}

/** Whether the stored edits differ from what the dialog would store. */
function changedSinceOpen(r: Review, edits: readonly MarkdownEdit[]): boolean {
	const stored = r.items.filter((it) => it.edit !== undefined);
	if (stored.length !== edits.length) return true;
	return edits.some((e) => r.items.find((it) => it.block === e.block)?.edit !== e.md);
}

function closeReview(): void {
	review.value = null;
	drafts.value = {};
	shown.value = PAGE;
	lookShown.value = PAGE;
}

/** Opens the review of version `n` (1 or more): the worker reads the paragraphs, with a working indicator. */
export async function openMarkdownReview(n: number): Promise<void> {
	if (n < 1 || versionText(n) === null) return;
	drafts.value = {};
	shown.value = PAGE;
	lookShown.value = PAGE;
	review.value = { n, loading: true, error: null, items: [], moved: 0, dropped: 0 };
	const id = 'md-review';
	beginWork(id, REVIEW.reading);
	try {
		await nextPaint();
		const r = await reviewMarkdown(n);
		if (review.peek()?.n !== n) return;
		review.value = r
			? { n, loading: false, error: null, ...r }
			: { n, loading: false, error: null, items: [], moved: 0, dropped: 0 };
	} catch (e) {
		if (review.peek()?.n === n)
			review.value = {
				n,
				loading: false,
				error: REVIEW.failed(faultReason(e)),
				items: [],
				moved: 0,
				dropped: 0,
			};
	} finally {
		endWork(id);
	}
}

async function save(r: Review): Promise<void> {
	const edits = pendingEdits(r);
	saving.value = true;
	try {
		await saveMarkdownEdits(r.n, edits);
		const text = versionText(r.n);
		if (text !== null)
			setMarkupHint(text, unsettledMoved(r.items, edits), unsettledDropped(r.items, edits));
		addNotice(REVIEW.saved(VERSIONS.version(String(r.n)), String(edits.length)));
		closeReview();
	} catch (e) {
		review.value = { ...r, error: HEADER.exportFailed((e as Error).message) };
	} finally {
		saving.value = false;
	}
}

const settledBy = (it: ReviewItem, edits: readonly MarkdownEdit[]): boolean =>
	edits.some((e) => e.block === it.block && e.base === it.base);

function unsettledMoved(items: readonly ReviewItem[], edits: readonly MarkdownEdit[]): number {
	return items.reduce(
		(n, it) => n + (settledBy(it, edits) ? 0 : it.spans.filter((x) => x.status === 'moved').length),
		0
	);
}

function unsettledDropped(items: readonly ReviewItem[], edits: readonly MarkdownEdit[]): number {
	return items.reduce(
		(n, it) =>
			n + (settledBy(it, edits) ? 0 : it.spans.filter((x) => x.status === 'dropped').length),
		0
	);
}

/* ---------------- the original, with its moved and dropped spans marked ---------------- */

/** The original's Markdown in pieces: plain, or marked where a span moved or was dropped (found in order by its text). */
function marked(original: string, spans: readonly ReviewSpan[]): ComponentChildren[] {
	const out: ComponentChildren[] = [];
	let at = 0;
	for (const x of spans) {
		if (x.status === 'carried') continue;
		const i = original.indexOf(x.before, at);
		if (i < 0) continue;
		if (i > at) out.push(original.slice(at, i));
		out.push(
			<mark class={`mdr-${x.status}`} data-status={x.status}>
				{x.before}
			</mark>
		);
		at = i + x.before.length;
	}
	if (at < original.length) out.push(original.slice(at));
	return out;
}

/* ---------------- the preview ---------------- */

function nodes(list: readonly InlineNode[]): ComponentChildren[] {
	return list.map((n) => {
		switch (n.t) {
			case 'text':
				return n.s;
			case 'code':
				return <code>{n.s}</code>;
			case 'em':
				return <em>{nodes(n.kids)}</em>;
			case 'strong':
				return <strong>{nodes(n.kids)}</strong>;
			case 'del':
				return <del>{nodes(n.kids)}</del>;
			default:
				return <span class="mdr-link">{nodes(n.kids)}</span>;
		}
	});
}

/* ---------------- one paragraph ---------------- */

function Item({ it }: { it: ReviewItem }) {
	const ta = useRef<HTMLTextAreaElement>(null);
	// the selection to put back once the toggled text is in the textarea (a layout effect: no frame in between)
	const reselect = useRef<{ start: number; end: number } | null>(null);
	useLayoutEffect(() => {
		const sel = reselect.current;
		const el = ta.current;
		if (!sel || !el) return;
		reselect.current = null;
		el.focus();
		el.setSelectionRange(sel.start, sel.end);
	});
	const value = currentMd(it);
	const para = String(it.para);
	const edited = value !== it.auto;
	const differs = plainOf(value) !== plainOf(it.auto);
	const set = (v: string) => {
		drafts.value = { ...drafts.value, [it.block]: v };
	};
	const apply = (mark: 'bold' | 'italic') => {
		const el = ta.current;
		if (!el) return;
		const r = toggleMark(value, { start: el.selectionStart, end: el.selectionEnd }, mark);
		if (r.md === value) return;
		reselect.current = r.selection;
		set(r.md);
	};
	const moved = it.spans.filter((x) => x.status === 'moved');
	const dropped = it.spans.filter((x) => x.status === 'dropped');
	return (
		<article
			class="mdr-item"
			data-testid="mdr-item"
			data-block={it.block}
			data-para={it.para}
			data-problem={problem(it)}
		>
			<h4 class="mdr-title">
				{REVIEW.para(para)}
				{moved.length > 0 ? <span class="mdr-chip mdr-chip-moved">{REVIEW.moved}</span> : null}
				{dropped.length > 0 ? (
					<span class="mdr-chip mdr-chip-dropped">{REVIEW.dropped}</span>
				) : null}
				{it.stale !== undefined ? (
					<span class="mdr-chip mdr-chip-stale">{REVIEW.staleChip}</span>
				) : null}
				{edited ? <span class="mdr-chip mdr-chip-edited">{REVIEW.edited}</span> : null}
			</h4>
			<div class="mdr-cols">
				<section aria-label={REVIEW.original}>
					<h5>{REVIEW.original}</h5>
					<p class="mdr-md" data-testid="mdr-original">
						{marked(it.original, it.spans)}
					</p>
				</section>
				<section aria-label={REVIEW.rewritten}>
					<h5>{REVIEW.rewritten}</h5>
					<div class="mdr-tools">
						<button
							type="button"
							class="btn btn-secondary btn-sm"
							data-testid="mdr-bold"
							title={REVIEW.boldHelp}
							aria-keyshortcuts="Control+B Meta+B"
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => apply('bold')}
						>
							<strong>{REVIEW.bold}</strong>
						</button>
						<button
							type="button"
							class="btn btn-secondary btn-sm"
							data-testid="mdr-italic"
							title={REVIEW.italicHelp}
							aria-keyshortcuts="Control+I Meta+I"
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => apply('italic')}
						>
							<em>{REVIEW.italic}</em>
						</button>
						<button
							type="button"
							class="btn btn-secondary btn-sm"
							data-testid="mdr-reset"
							disabled={!edited}
							onClick={() => set(it.auto)}
						>
							{REVIEW.reset}
						</button>
					</div>
					<textarea
						ref={ta}
						class="textarea mdr-edit"
						data-testid="mdr-edit"
						aria-label={REVIEW.textarea(para)}
						rows={Math.min(14, Math.max(3, Math.ceil(value.length / 60) + 1))}
						value={value}
						onInput={(e) => set((e.currentTarget as HTMLTextAreaElement).value)}
						onKeyDown={(e) => {
							if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
							const k = e.key.toLowerCase();
							if (k !== 'b' && k !== 'i') return;
							e.preventDefault();
							apply(k === 'b' ? 'bold' : 'italic');
						}}
					/>
					<p class="mdr-preview" data-testid="mdr-preview">
						<span class="mdr-preview-label">{REVIEW.preview}: </span>
						{nodes(parseInline(value))}
					</p>
					{differs ? (
						<output class="mdr-warn" data-testid="mdr-differs">
							{REVIEW.differs}
						</output>
					) : null}
				</section>
			</div>
			{moved.length + dropped.length > 0 ? (
				<ul class="mdr-spans" data-testid="mdr-spans">
					{it.spans.map((x) =>
						x.status === 'moved' ? (
							<li key={`${x.status}-${x.before}`}>{REVIEW.spanMoved(x.before, x.after ?? '')}</li>
						) : x.status === 'dropped' ? (
							<li key={`${x.status}-${x.before}`}>{REVIEW.spanDropped(x.before)}</li>
						) : null
					)}
				</ul>
			) : null}
			{it.stale !== undefined ? (
				<div class="mdr-warn" data-testid="mdr-stale">
					<p>{REVIEW.stale}</p>
					<p class="mono">{REVIEW.staleSee(it.stale)}</p>
					<button
						type="button"
						class="btn btn-secondary btn-sm"
						data-testid="mdr-stale-use"
						onClick={() => set(it.stale ?? it.auto)}
					>
						{REVIEW.staleUse}
					</button>
				</div>
			) : null}
		</article>
	);
}

/* ---------------- the dialog ---------------- */

function Body({ r }: { r: Review }) {
	if (r.loading) return <p class="dim">{REVIEW.reading}…</p>;
	if (r.error)
		return (
			<div data-testid="mdr-error">
				<p class="mdr-warn">{r.error}</p>
				<button
					type="button"
					class="btn btn-secondary btn-sm"
					onClick={() => void openMarkdownReview(r.n)}
				>
					{REVIEW.retry}
				</button>
			</div>
		);
	if (r.items.length === 0) return <p data-testid="mdr-empty">{REVIEW.noParagraphs}</p>;
	const look = r.items.filter(problem);
	const rest = r.items.filter((it) => !problem(it));
	return (
		<>
			<p data-testid="mdr-summary">
				{REVIEW.summary(String(r.items.length), String(r.moved), String(r.dropped))}
			</p>
			<h3 class="mdr-h3">{REVIEW.look(String(look.length))}</h3>
			{look.length === 0 ? <p class="dim">{REVIEW.lookNone}</p> : null}
			<div data-testid="mdr-look">
				{look.slice(0, lookShown.value).map((it) => (
					<Item key={it.block} it={it} />
				))}
			</div>
			{look.length > lookShown.value ? (
				<button
					type="button"
					class="btn btn-secondary btn-sm"
					data-testid="mdr-look-more"
					onClick={() => {
						lookShown.value += PAGE;
					}}
				>
					{REVIEW.more(String(Math.min(PAGE, look.length - lookShown.value)))}
				</button>
			) : null}
			{rest.length > 0 ? (
				<details class="mdr-rest" data-testid="mdr-rest">
					<summary>{REVIEW.rest(String(rest.length))}</summary>
					{rest.slice(0, shown.value).map((it) => (
						<Item key={it.block} it={it} />
					))}
					{rest.length > shown.value ? (
						<button
							type="button"
							class="btn btn-secondary btn-sm"
							data-testid="mdr-more"
							onClick={() => {
								shown.value += PAGE;
							}}
						>
							{REVIEW.more(String(Math.min(PAGE, rest.length - shown.value)))}
						</button>
					) : null}
				</details>
			) : null}
		</>
	);
}

/** The review dialog; mounted once, opened by `openMarkdownReview`. */
export function MarkdownReview() {
	const r = review.value;
	const edits = r && !r.loading ? pendingEdits(r) : [];
	const dirty = r ? changedSinceOpen(r, edits) : false;
	const label = r ? VERSIONS.version(String(r.n)) : '';
	return (
		<Dialog
			title={REVIEW.title(label)}
			open={r !== null}
			onClose={closeReview}
			actions={
				<>
					{r && !r.loading ? (
						<span class="dim mdr-pending" data-testid="mdr-pending">
							{REVIEW.pending(String(edits.length))}
						</span>
					) : null}
					<button
						type="button"
						class="btn btn-secondary"
						data-testid="mdr-close"
						onClick={closeReview}
					>
						{dirty ? REVIEW.closeDiscard : REVIEW.close}
					</button>
					<button
						type="button"
						class="btn btn-primary"
						data-testid="mdr-save"
						disabled={!r || r.loading || !dirty || saving.value}
						onClick={() => r && void save(r)}
					>
						{REVIEW.save}
					</button>
				</>
			}
		>
			{r ? (
				<div class="mdr" data-testid="mdr">
					<p class="dim">{REVIEW.intro}</p>
					<Body r={r} />
				</div>
			) : null}
		</Dialog>
	);
}
